import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Caption,
  IconButton,
  Section,
  Textarea,
  VisuallyHidden,
} from '@telegram-apps/telegram-ui';
import type { QuestionInboxItem, ReportReason } from '@veylme/shared';

import {
  AlertTriangleIcon,
  BanIcon,
  DoubleCheckIcon,
  EyeIcon,
  EyeOffIcon,
  FlagIcon,
  SendIcon,
  ShareIcon,
} from '../ui/icons.js';
import { useT } from '../hooks/useT.js';
import { useLocale } from '../i18n/LocaleContext.js';
import { clearDraft, readDraft, saveDraft } from '../lib/launchContext.js';
import { hapticImpact, hapticNotification } from '../lib/tg.js';
import styles from './QuestionCard.module.css';

const ANSWER_MAX = 1000;
/** Потолок авто-роста поля ответа (px) — дальше включается внутренний скролл. */
const COMPOSER_MAX_H = 160;
/** Пауза перед записью черновика ответа — пишем не на каждую букву (work-038 §5.3). */
const DRAFT_DEBOUNCE_MS = 500;

/**
 * QuestionCard — карточка одного вопроса в виде чат-переписки (Веха 13.x).
 *
 * Дизайн: вопрос — входящий «пузырь» (слева), ответ — исходящий (справа, как в
 * Telegram, с временем и двойной галочкой). Внутри пузыря вопроса снизу — мелкие
 * инструменты автора (скрыть/жалоба/блок) слева и дата-время справа.
 *
 * Ответ — инлайн: у неотвеченных по центру ссылка «Ответить» → разворачивается
 * поле-композер (нативная Textarea, эмодзи с клавиатуры) с кнопкой-самолётиком.
 * Отправка идёт через onSubmitAnswer (POST /answers делает InboxScreen, он же
 * патчит item) — после успеха появляется пузырь ответа и ссылка «Поделиться».
 *
 * CANON G2: все мутации (hide/report/block/answer/share) — ТОЛЬКО из onClick.
 * CANON SP4/D1: клиент оперирует ТОЛЬКО questionId; тело ответа — questionId+text.
 *
 * work-038: набранный ответ автора переживает холодный перезапуск приложения —
 * типичная жертва протухшего пропуска Telegram (долго открытое приложение).
 * Черновик пишется в `lib/launchContext.ts` с дебаунсом и адресуется по
 * `questionId`; подставляется на mount ТОЛЬКО для этого же вопроса.
 */
export interface QuestionCardProps {
  item: QuestionInboxItem;
  onHide: (questionId: string, hidden: boolean) => Promise<void>;
  onReport: (questionId: string, reason: ReportReason) => Promise<void>;
  onBlock: (questionId: string, blocked: boolean) => Promise<void>;
  /**
   * Отправка ответа. POST /answers + патч item делает InboxScreen; на ошибке
   * бросает Error с готовым к показу `message`. После успеха item становится
   * answered и композер сам сворачивается (ререндер).
   */
  onSubmitAnswer: (questionId: string, text: string) => Promise<void>;
  /** Открыть экран шеринга карточки ответа (петля роста). */
  onShare: (answer: { id: string; cardImageUrl: string | null }) => void;
}

type ActionState =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'notice'; tone: 'info' | 'error'; text: string };

/** Дата + время без секунд: «29.05.2026, 13:00» — внутри пузыря справа снизу. */
function formatDateTime(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleString(locale === 'ru' ? 'ru-RU' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function QuestionCard({
  item,
  onHide,
  onReport,
  onBlock,
  onSubmitAnswer,
  onShare,
}: QuestionCardProps) {
  const t = useT();
  const locale = useLocale();
  const [action, setAction] = useState<ActionState>({ kind: 'idle' });
  const [reportOpen, setReportOpen] = useState(false);
  // work-038 (ревью нашло дефект 2): восстановленный черновик читаем с диска
  // РОВНО ОДИН РАЗ при монтировании и переиспользуем его для ДВУХ начальных
  // состояний — и текста поля, и того, раскрыт ли композер. Ref-инициализация
  // «if пусто — заполнить» выполняется на первом рендере и больше не трогается
  // (в отличие от ленивого инициализатора useState, к которому нельзя
  // обратиться из другого useState), поэтому readDraft зовётся один раз, а не
  // дважды. Сам восстановленный текст нужен ниже composerOpen: раньше он
  // всегда стартовал `false`, и восстановленный ответ лежал в состоянии, но
  // композер был свёрнут — человек видел обычную кнопку «Ответить» и решал,
  // что набранный текст пропал.
  const initialAnswerRef = useRef<string | undefined>(undefined);
  if (initialAnswerRef.current === undefined) {
    initialAnswerRef.current =
      readDraft({ screen: 'ANSWER', questionId: item.id }) ?? '';
  }
  const [composerOpen, setComposerOpen] = useState(
    () => initialAnswerRef.current!.trim() !== '',
  );
  const [answerText, setAnswerText] = useState(() => initialAnswerRef.current!);
  // work-038: таймер дебаунса записи черновика — держим в ref, а не в state
  // (переустановка таймера не должна вызывать лишний ререндер).
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // work-038: «черновик потреблён» — выставляется в момент clearDraft() ниже и
  // гасит отложенную запись, если таймер всё же успел встать в очередь до
  // clearTimeout в handleSend (защита от гонки, см. докстринг файла).
  const draftConsumedRef = useRef(false);

  // work-038: дебаунс записи черновика — пишем не на каждую букву, а спустя
  // DRAFT_DEBOUNCE_MS после последней правки. Cleanup гасит таймер и при
  // каждой новой правке, и при размонтировании — протечки в следующий тест
  // быть не должно.
  useEffect(() => {
    draftTimerRef.current = setTimeout(() => {
      if (draftConsumedRef.current) return;
      saveDraft({ screen: 'ANSWER', questionId: item.id, text: answerText });
    }, DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(draftTimerRef.current);
  }, [item.id, answerText]);

  // work-038 (ревью нашло дефект 3): ref на textarea композера — нужен, чтобы
  // применить autoGrow сразу при раскрытии композера с восстановленным
  // черновиком (см. эффект ниже).
  const answerTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  // work-038 (дефект 3): восстановленный многострочный черновик должен
  // открыться уже разросшимся, а не схлопнутым в rows={1} с внутренним
  // скроллом. Раньше autoGrow звался только из onChange — при восстановлении
  // текста композер стартует УЖЕ заполненным (см. дефект 2 выше), onChange не
  // происходит, и человек видел один ряд, пока не нажимал клавишу. Считаем
  // высоту сразу при раскрытии композера, если в нём уже есть текст; сама
  // логика autoGrow и потолок COMPOSER_MAX_H не меняются.
  useEffect(() => {
    if (composerOpen && answerTextareaRef.current && answerText.trim() !== '') {
      autoGrow(answerTextareaRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только при раскрытии, не на каждую букву (это уже делает onChange)
  }, [composerOpen]);

  const isHidden = item.status === 'hidden';
  const isPending = item.status === 'pending';
  const isFlagged = item.moderationFlag === 'flagged';
  // Заблокированный вопрос: зеркало серверного представления (status='removed' +
  // moderationFlag='blocked'). Кнопка-«бан» в этом состоянии разблокирует.
  const isBlocked = item.status === 'removed' || item.moderationFlag === 'blocked';
  const hasAnswer = item.answer !== null && item.answer.text !== null;
  const busy = action.kind === 'busy';

  const REPORT_REASONS: { reason: ReportReason; label: string }[] = [
    { reason: 'spam', label: t.qcard_reason_spam },
    { reason: 'harassment', label: t.qcard_reason_harassment },
    { reason: 'sexual', label: t.qcard_reason_sexual },
    { reason: 'other', label: t.qcard_reason_other },
  ];

  // Generic wrapper: переводит карточку в busy, ловит ошибку, показывает notice.
  // `successText` (опц.) — короткое подтверждение успеха (жалоба/блок), иначе
  // действие просто завершается без шума. Подтверждение авто-гаснет через 3с.
  async function run(
    fn: () => Promise<void>,
    errText: string,
    successText?: string,
  ): Promise<void> {
    setAction({ kind: 'busy' });
    setReportOpen(false);
    try {
      await fn();
      if (successText) {
        setAction({ kind: 'notice', tone: 'info', text: successText });
        setTimeout(() => setAction({ kind: 'idle' }), 3000);
      } else {
        setAction({ kind: 'idle' });
      }
    } catch {
      setAction({ kind: 'notice', tone: 'error', text: errText });
      // work-024 D4: авто-сброс ошибки через 5 секунд — уведомление не висит вечно.
      setTimeout(() => setAction({ kind: 'idle' }), 5000);
    }
  }

  // Авто-рост текстового поля по содержимому (до COMPOSER_MAX_H, дальше скролл).
  function autoGrow(el: HTMLTextAreaElement): void {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_H)}px`;
  }

  async function handleSend() {
    const trimmed = answerText.trim();
    if (trimmed.length === 0 || busy) return;
    // work-038 (ревью нашло дефект 1): flush-then-send. Сначала гасим
    // отложенную запись, ЗАТЕМ синхронно пишем черновик на диск и только
    // потом шлём запрос. Раньше здесь стоял голый clearTimeout — если
    // отправка проваливалась (в первую очередь ради 401 всё и затевалось),
    // черновик нигде не оставался: catch-ветка saveDraft не зовёт, а эффект
    // дебаунса не перезапустится (его deps [item.id, answerText] не
    // поменялись), и при 401 глобальный перехват мгновенно подменяет экран на
    // SessionExpiredScreen — карточка размонтируется, набранный ответ автора
    // исчезал бесследно ровно в момент, ради которого весь work-038 и
    // затевался. Запись ДО сетевого вызова это чинит и НЕ возвращает старый
    // дефект «отправленный текст воскресает»: отложенного таймера уже нет
    // (clearTimeout выше), а при успехе ниже clearDraft стирает запись —
    // инвариант «после успешной отправки в хранилище черновика нет» держится.
    clearTimeout(draftTimerRef.current);
    saveDraft({ screen: 'ANSWER', questionId: item.id, text: answerText });
    setAction({ kind: 'busy' });
    try {
      await onSubmitAnswer(item.id, trimmed);
      hapticNotification('success');
      // work-038: черновик потреблён — стираем и гасим отложенную запись.
      draftConsumedRef.current = true;
      clearDraft({ screen: 'ANSWER', questionId: item.id });
      setAnswerText('');
      setComposerOpen(false);
      setAction({ kind: 'idle' });
    } catch (err: unknown) {
      hapticNotification('error');
      const message =
        err instanceof Error && err.message ? err.message : t.answer_error_send;
      setAction({ kind: 'notice', tone: 'error', text: message });
      // Авто-сброс ошибки через 5 секунд.
      setTimeout(() => setAction({ kind: 'idle' }), 5000);
    }
  }

  const statusLabel = isPending
    ? t.qcard_status_new
    : isHidden
      ? t.qcard_status_hidden
      : t.qcard_status_answered;

  const canSend = answerText.trim().length > 0 && !busy;

  return (
    <Section>
      <div className={styles.cardInner}>
        {/* Статус словом — только для screen-reader'ов. */}
        <VisuallyHidden>{statusLabel}</VisuallyHidden>

        {/* Вопрос — входящий пузырь (слева). Внутри снизу: инструменты автора
            (слева) и дата-время (справа). Помеченный модерацией — янтарная рамка
            + мелкая «легенда» сверху (вместо кричащей красной плашки). */}
        <div className={styles.rowIn}>
          <div
            className={`${styles.bubble} ${styles.bubbleIn}${hasAnswer ? ` ${styles.bubbleConv}` : ''}${isHidden ? ` ${styles.bubbleHidden}` : ''}${isFlagged ? ` ${styles.bubbleFlagged}` : ''}`}
          >
            {isFlagged && (
              <span
                className={styles.flagBadge}
                title={t.qcard_moderation_flagged}
                aria-label={t.qcard_moderation_flagged}
              >
                <AlertTriangleIcon />
                {t.qcard_moderation_flagged}
              </span>
            )}
            <p className={styles.msgText}>{item.text}</p>
            <div className={styles.bubbleFooter}>
              <div className={styles.tools}>
                <IconButton
                  mode="plain"
                  size="s"
                  aria-label={isHidden ? t.qcard_show_btn : t.qcard_hide_btn}
                  title={isHidden ? t.qcard_show_btn : t.qcard_hide_btn}
                  disabled={busy}
                  onClick={() => {
                    hapticImpact('light');
                    void run(() => onHide(item.id, !isHidden), t.qcard_error_hide);
                  }}
                >
                  {isHidden ? <EyeIcon /> : <EyeOffIcon />}
                </IconButton>
                <IconButton
                  mode="plain"
                  size="s"
                  aria-label={t.qcard_report_btn}
                  title={t.qcard_report_btn}
                  disabled={busy}
                  onClick={() => {
                    hapticImpact('light');
                    setReportOpen((v) => !v);
                  }}
                >
                  <FlagIcon />
                </IconButton>
                <IconButton
                  mode="plain"
                  size="s"
                  aria-label={isBlocked ? t.qcard_unblock_btn : t.qcard_block_btn}
                  title={isBlocked ? t.qcard_unblock_btn : t.qcard_block_btn}
                  disabled={busy}
                  onClick={() => {
                    hapticImpact('medium');
                    void run(
                      () => onBlock(item.id, !isBlocked),
                      t.qcard_error_block,
                      isBlocked ? t.qcard_unblock_done : t.qcard_block_done,
                    );
                  }}
                >
                  <BanIcon />
                </IconButton>
              </div>
              <span className={styles.time}>
                {formatDateTime(item.createdAt, locale)}
              </span>
            </div>
          </div>
        </div>

        {reportOpen && (
          <div className={styles.reportSection}>
            <Caption Component="p" className={styles.reportHint}>
              {t.qcard_report_reason_label}
            </Caption>
            <div className={styles.reportBtns}>
              {REPORT_REASONS.map(({ reason, label }) => (
                <Button
                  key={reason}
                  mode="bezeled"
                  size="s"
                  disabled={busy}
                  onClick={() =>
                    run(
                      () => onReport(item.id, reason),
                      t.qcard_error_report,
                      t.qcard_report_done,
                    )
                  }
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Ответ — исходящий пузырь (справа, с временем и двойной галочкой). */}
        {hasAnswer && item.answer !== null && (
          <div className={styles.rowOut}>
            <div className={`${styles.bubble} ${styles.bubbleOut} ${styles.bubbleConv}`}>
              <p className={styles.msgText}>{item.answer.text}</p>
              <div className={`${styles.bubbleFooter} ${styles.footerOut}`}>
                <span className={styles.timeOut}>
                  {formatDateTime(item.answer.createdAt, locale)}
                </span>
                <DoubleCheckIcon className={styles.readMark} />
              </div>
            </div>
          </div>
        )}

        {/* Под ответом — «Поделиться» (экран шеринга карточки): bezeled-кнопка с
            иконкой шеринга, выровнена по левому краю (как «Ответить»). */}
        {hasAnswer && item.answer !== null && (
          <div className={styles.actionCenter}>
            <Button
              mode="bezeled"
              size="s"
              before={<ShareIcon width={18} height={18} />}
              onClick={() =>
                onShare({
                  id: item.answer!.id,
                  cardImageUrl: item.answer!.cardImageUrl,
                })
              }
            >
              {t.qcard_share_btn}
            </Button>
          </div>
        )}

        {/* Неотвеченные: кнопка «Ответить» по центру (bezeled, как «Пригласить
            через чат») → разворачивает композер. */}
        {isPending && !composerOpen && (
          <div className={styles.actionCenter}>
            <Button
              mode="bezeled"
              size="s"
              onClick={() => setComposerOpen(true)}
            >
              {t.qcard_answer_btn}
            </Button>
          </div>
        )}

        {isPending && composerOpen && (
          <div className={styles.composer}>
            <Textarea
              ref={answerTextareaRef}
              autoFocus
              value={answerText}
              maxLength={ANSWER_MAX}
              placeholder={t.answer_placeholder}
              disabled={busy}
              rows={1}
              onChange={(e) => {
                setAnswerText(e.target.value);
                autoGrow(e.target);
              }}
            />
            <IconButton
              mode="plain"
              size="m"
              aria-label={t.qcard_answer_send}
              title={t.qcard_answer_send}
              disabled={!canSend}
              className={styles.sendBtn}
              onClick={() => {
                hapticImpact('light');
                void handleSend();
              }}
            >
              <SendIcon />
            </IconButton>
          </div>
        )}

        {action.kind === 'notice' && (
          <Caption
            Component="p"
            role={action.tone === 'error' ? 'alert' : undefined}
            className={`${styles.notice} ${action.tone === 'error' ? styles.noticeError : styles.noticeInfo}`}
          >
            {action.text}
          </Caption>
        )}
      </div>
    </Section>
  );
}
